import { useState } from 'react';
import {
  ActionIcon,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconPlus, IconSchool, IconTrash } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { unwrap } from '../../api/client';
import { createChild, deleteChild, setChildPin, updateChild } from '../../api/gen';
import type { Child } from '../../api/gen';
import { ChildAvatar } from '../../components/ChildAvatar';
import { SchoolFields, type SchoolDraft, type SchoolValue } from '../../components/SchoolFields';
import { schooling } from '../../lib/school';
import { useOrg, useSession } from '../../lib/session';
import { accent } from '../../theme';

// The roster, and the two things only a parent may do to it: add a child, and
// remove one. A child cannot read or set any PIN, including their own — that
// is a rule in the service and a test per route, and this page is simply where
// it becomes visible.
export function ChildrenPage() {
  const { pairing, refreshChildren } = useSession();
  const org = useOrg();
  const [adding, setAdding] = useState(false);
  const [pinFor, setPinFor] = useState<{ id: string; name: string } | null>(null);
  // The child stays set after the dialog closes, so its fade-out shows them
  // rather than an empty form.
  const [schoolFor, setSchoolFor] = useState<Child | null>(null);
  const [schoolOpen, setSchoolOpen] = useState(false);

  const remove = useMutation({
    mutationFn: (id: string) => unwrap(deleteChild({ path: { id }, headers: org })),
    onSuccess: async () => {
      await refreshChildren();
      notifications.show({ message: 'Profil gelöscht.' });
    },
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Kinder</Title>
        <Button leftSection={<IconPlus size={18} />} onClick={() => setAdding(true)}>
          Kind hinzufügen
        </Button>
      </Group>

      <Text size="sm" c="dimmed">
        Ein Kind ist ein Sitzplatz im Abo. Ein gelöschtes Profil nimmt seinen Lernverlauf mit —
        die Sitzungen, die Gespräche, alles.
      </Text>

      {(pairing?.children ?? []).map((c) => (
        <Card key={c.id} withBorder padding="md">
          <Group justify="space-between">
            <Link to={`/eltern/kinder/${c.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <Group gap="sm">
                <ChildAvatar name={c.name} mode={c.mode} size={36} />
                <div>
                  <Text fw={600}>{c.name}</Text>
                  <Text size="xs" c="dimmed">
                    {[accent(c.mode).label, `${c.age} Jahre`, schooling(c)].filter(Boolean).join(' · ')}
                    {c.gradeAdvanced && !c.needsSchool ? ' (zum Schuljahr hochgezählt)' : ''}
                    {c.lockedOut ? ' · PIN gerade gesperrt' : ''}
                  </Text>
                </div>
              </Group>
            </Link>
            <Group gap="xs">
              <Button
                size="xs"
                variant={c.needsSchool || !c.grade ? 'filled' : 'light'}
                color={c.needsSchool || !c.grade ? 'orange' : undefined}
                leftSection={c.needsSchool ? <IconAlertTriangle size={14} /> : <IconSchool size={14} />}
                onClick={() => {
                  setSchoolFor(c);
                  setSchoolOpen(true);
                }}
              >
                {c.needsSchool ? 'Neue Schule wählen' : c.grade ? 'Schule' : 'Schule angeben'}
              </Button>
              <Button size="xs" variant="light" onClick={() => setPinFor({ id: c.id, name: c.name })}>
                PIN ändern
              </Button>
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label={`${c.name} löschen`}
                loading={remove.isPending}
                onClick={() => {
                  if (confirm(`${c.name} und den gesamten Lernverlauf löschen?`)) remove.mutate(c.id);
                }}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Group>
          </Group>
          {c.needsSchool && (
            <Text size="xs" c="orange.8" mt="xs">
              Neues Schuljahr: {c.name} ist jetzt in Klasse {c.grade}. Welche Schule besucht{' '}
              {c.name}? Danach passen Sokrates, die Fächer und die Arbeitsblätter wieder.
            </Text>
          )}
        </Card>
      ))}

      <AddChild opened={adding} onClose={() => setAdding(false)} />
      <ChangePin child={pinFor} onClose={() => setPinFor(null)} />
      <ChangeSchool child={schoolFor} opened={schoolOpen} onClose={() => setSchoolOpen(false)} />
    </Stack>
  );
}

function AddChild({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const org = useOrg();
  const { refreshChildren } = useSession();
  const [name, setName] = useState('');
  const [age, setAge] = useState<number | string>(9);
  const [mode, setMode] = useState('junior');
  const [pin, setPin] = useState('');
  const [school, setSchool] = useState<SchoolValue>({ school: 'grundschule', grade: 3 });

  const create = useMutation({
    mutationFn: () =>
      unwrap(
        createChild({
          headers: org,
          body: {
            name: name.trim(),
            age: Number(age),
            mode: mode as 'junior' | 'senior',
            pin,
            school: school.school,
            grade: school.grade,
          },
        }),
      ),
    onSuccess: async () => {
      await refreshChildren();
      setName('');
      setPin('');
      onClose();
    },
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Kind hinzufügen">
      <Stack>
        <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <NumberInput label="Alter" min={3} max={19} value={age} onChange={setAge} />
        <div>
          <SchoolFields value={school} onChange={setSchool} />
          <Text size="xs" c="dimmed" mt={4}>
            Danach richten sich Sokrates' Fragen, die Fächer und die Arbeitsblätter. Die Klasse
            zählt jeden 1. August von selbst eine weiter.
          </Text>
        </div>
        <div>
          <Text size="sm" fw={500} mb={4}>
            Modus
          </Text>
          <SegmentedControl
            fullWidth
            value={mode}
            onChange={setMode}
            data={[
              { value: 'junior', label: 'Junior' },
              { value: 'senior', label: 'Senior' },
            ]}
          />
          <Text size="xs" c="dimmed" mt={4}>
            Junior fragt in kürzeren Schritten und einfacherer Sprache. Ab etwa zwölf passt
            Senior besser.
          </Text>
        </div>
        <TextInput
          label="PIN"
          description="Vier Ziffern. Ohne sie gäbe es keine Möglichkeit, sich als dieses Kind anzumelden."
          inputMode="numeric"
          maxLength={4}
          value={pin}
          onChange={(e) => setPin(e.currentTarget.value.replace(/\D/g, ''))}
        />
        <Button
          disabled={!name.trim() || pin.length !== 4}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          Anlegen
        </Button>
      </Stack>
    </Modal>
  );
}

function ChangePin({
  child,
  onClose,
}: {
  child: { id: string; name: string } | null;
  onClose: () => void;
}) {
  const org = useOrg();
  const [pin, setPin] = useState('');

  const save = useMutation({
    mutationFn: () => unwrap(setChildPin({ path: { id: child!.id }, headers: org, body: { pin } })),
    onSuccess: () => {
      setPin('');
      onClose();
      notifications.show({ message: 'PIN geändert. Alle offenen Anmeldungen wurden beendet.' });
    },
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  return (
    <Modal opened={!!child} onClose={onClose} title={`PIN für ${child?.name ?? ''}`}>
      <Stack>
        <Text size="sm" c="dimmed">
          Eine neue PIN beendet jede Anmeldung, die mit der alten geöffnet wurde. Wer eine PIN
          ändert, hat meist entschieden, dass jemand nicht mehr angemeldet sein soll.
        </Text>
        <TextInput
          label="Neue PIN"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          onChange={(e) => setPin(e.currentTarget.value.replace(/\D/g, ''))}
        />
        <Button disabled={pin.length !== 4} loading={save.isPending} onClick={() => save.mutate()}>
          Speichern
        </Button>
      </Stack>
    </Modal>
  );
}

function ChangeSchool({
  child,
  opened,
  onClose,
}: {
  child: Child | null;
  opened: boolean;
  onClose: () => void;
}) {
  const org = useOrg();
  const { refreshChildren } = useSession();
  const [value, setValue] = useState<SchoolValue | null>(null);
  // Open on what the child has now. After grade 4 the school type stays open
  // until the parent picks it: Mittelschule, Realschule and Gymnasium are each
  // common, and a guess would fit Sokrates to the wrong plan.
  const shown: SchoolDraft =
    value ??
    (child?.school && child.grade
      ? { school: child.school, grade: child.grade }
      : child?.grade
        ? { school: null, grade: child.grade }
        : { school: 'grundschule', grade: 3 });

  const save = useMutation({
    mutationFn: () =>
      unwrap(updateChild({ path: { id: child!.id }, headers: org, body: { school: shown.school!, grade: shown.grade } })),
    onSuccess: async () => {
      await refreshChildren();
      setValue(null);
      onClose();
      notifications.show({ message: 'Schule gespeichert.' });
    },
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  return (
    <Modal
      opened={opened}
      onClose={() => {
        setValue(null);
        onClose();
      }}
      title={`Schule von ${child?.name ?? ''}`}
    >
      <Stack>
        <SchoolFields value={shown} onChange={setValue} />
        <Text size="xs" c="dimmed">
          Die Klasse gilt für dieses Schuljahr und zählt am 1. August eine weiter. Nach einer
          Ehrenrunde oder einem Schulwechsel hier einfach korrigieren.
        </Text>
        <Button disabled={!shown.school} loading={save.isPending} onClick={() => save.mutate()}>
          Speichern
        </Button>
      </Stack>
    </Modal>
  );
}
